import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Badge, Button } from '@react-spectrum/s2'
import {
  ArrowUpDown,
  Database,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import {
  createGlobalPlan,
  deleteGlobalPlan,
  getGlobalPlans,
  updateGlobalPlan,
} from '@rpc/admin.rpc'

export const Route = createFileRoute('/admin/plans')({
  loader: async () => {
    return { plans: await getGlobalPlans() }
  },
  component: GlobalPlansPage,
})

type EditablePlan = ReturnType<typeof normalizePlanForEditor>

function GlobalPlansPage() {
  const { plans } = Route.useLoaderData()
  const router = useRouter()
  const [boardPlans, setBoardPlans] = useState(() =>
    [...plans].sort(comparePlans).map(normalizePlanForEditor),
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const orderedPlans = [...boardPlans].sort(comparePlans)

  const handleSaveAll = async () => {
    setIsSaving(true)
    try {
      await Promise.all(
        boardPlans.map((plan) =>
          updateGlobalPlan({
            data: serializePlanForSave(plan),
          }),
        ),
      )
      router.invalidate()
    } catch {
      alert('Failed to save plan changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreatePlan = async () => {
    const newPlan = buildNewPlanDraft(boardPlans)
    setIsCreating(true)
    try {
      await createGlobalPlan({
        data: serializePlanForSave(newPlan),
      })
      setBoardPlans((current) => [...current, newPlan])
      router.invalidate()
    } catch {
      alert('Failed to create plan')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeletePlan = async (id: string) => {
    if (!confirm('Delete this plan? This cannot be undone.')) return
    try {
      await deleteGlobalPlan({ data: { id } })
      setBoardPlans((current) => current.filter((plan) => plan.id !== id))
      router.invalidate()
    } catch {
      alert('Failed to delete plan')
    }
  }

  const updatePlanField = <TKey extends keyof EditablePlan>(
    id: string,
    key: TKey,
    value: EditablePlan[TKey],
  ) => {
    setBoardPlans((current) =>
      current.map((plan) =>
        plan.id === id ? { ...plan, [key]: value } : plan,
      ),
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1920px] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 rounded-[28px] border border-neutral-200 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_48%),linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(247,250,252,0.96))] p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)] dark:border-neutral-800 dark:bg-neutral-950">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
              <ArrowUpDown className="h-3.5 w-3.5" />
              Comparative plan matrix
            </div>
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                Global subscription plans
              </h2>
              <p className="mt-2 text-neutral-600 dark:text-neutral-300">
                Edit the plan table row by row. The left column defines the
                feature once, and each plan column only carries the value.
              </p>
            </div>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white/80 px-4 py-3 text-sm text-neutral-600 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/80 dark:text-neutral-300">
            <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-neutral-100">
              <Database className="h-4 w-4" />
              Source of truth
            </div>
            <p className="mt-1">
              The matrix edits rows in <code>plans</code>. Create and delete
              apply immediately. Field edits are saved together.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="secondary"
            fillStyle="outline"
            onPress={handleCreatePlan}
            isDisabled={isCreating || isSaving}
          >
            {isCreating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Create plan
          </Button>
          <Button
            variant="primary"
            onPress={handleSaveAll}
            isDisabled={isSaving || isCreating}
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save all plan changes
          </Button>
        </div>
      </div>

      <div className="w-full overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.06)] dark:border-neutral-800 dark:bg-neutral-950">
        <div className="border-b border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                Plan comparison table
              </h3>
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
                Wider plan columns, fewer repeated labels, and a single
                left-hand feature index.
              </p>
            </div>
            <div className="space-y-1 text-right">
              <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                {orderedPlans.length} plans
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                Scroll sideways to compare all tiers.
              </div>
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto">
          <div className="pointer-events-none absolute inset-y-0 right-0 z-20 hidden w-10 bg-gradient-to-l from-white to-transparent dark:from-neutral-950 lg:block" />
          <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
            <colgroup>
              <col style={{ width: 220 }} />
              {orderedPlans.map((plan) => (
                <col key={plan.id} style={{ width: 240 }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-30">
              <tr>
                <th className="sticky left-0 z-40 border-b border-r border-neutral-200 bg-neutral-50 px-4 py-4 text-left align-top dark:border-neutral-800 dark:bg-neutral-950">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                    Feature
                  </div>
                  <div className="mt-2 text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Plan fields
                  </div>
                  <p className="mt-1 text-xs leading-5 text-neutral-600 dark:text-neutral-300">
                    One row per editable property.
                  </p>
                </th>
                {orderedPlans.map((plan) => (
                  <PlanHeaderCell
                    key={plan.id}
                    plan={plan}
                    onDelete={() => handleDeletePlan(plan.id)}
                    isSaving={isSaving}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_SECTIONS.map((section) => (
                <PlanSectionBlock
                  key={section.title}
                  section={section}
                  plans={orderedPlans}
                  onChange={updatePlanField}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function PlanHeaderCell({
  plan,
  onDelete,
  isSaving,
}: {
  plan: EditablePlan
  onDelete: () => void
  isSaving: boolean
}) {
  return (
    <th className="border-b border-l border-neutral-200 bg-white px-4 py-4 text-left align-top dark:border-neutral-800 dark:bg-neutral-950">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-neutral-900 dark:text-neutral-100">
              {plan.displayName || formatPlanName(plan.name) || 'Untitled plan'}
            </div>
            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-neutral-500">
              {plan.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onDelete}
            disabled={isSaving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 transition hover:border-red-300 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:border-red-800 dark:hover:text-red-400"
            aria-label={`Delete ${plan.displayName || plan.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={plan.isPublic ? 'accent' : 'neutral'}
            fillStyle="subtle"
          >
            {plan.isPublic ? 'Public' : 'Internal'}
          </Badge>
          <span className="rounded-full bg-neutral-100 px-2 py-1 text-[11px] text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
            {plan.priceLabel || 'No price label'}
          </span>
          <span className="rounded-full bg-blue-50 px-2 py-1 text-[11px] text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            Sort {plan.sortOrder}
          </span>
        </div>
      </div>
    </th>
  )
}

function PlanSectionBlock({
  section,
  plans,
  onChange,
}: {
  section: PlanSection
  plans: Array<EditablePlan>
  onChange: <TKey extends keyof EditablePlan>(
    id: string,
    key: TKey,
    value: EditablePlan[TKey],
  ) => void
}) {
  return (
    <>
      <tr>
        <td
          colSpan={plans.length + 1}
          className="border-b border-t border-neutral-200 bg-neutral-100/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/70"
        >
          {section.title}
        </td>
      </tr>
      {section.rows.map((row) => (
        <tr key={row.key}>
          <th className="sticky left-0 z-20 border-b border-r border-neutral-200 bg-white px-4 py-4 text-left align-top dark:border-neutral-800 dark:bg-neutral-950">
            <div className="font-medium text-neutral-900 dark:text-neutral-100">
              {row.label}
            </div>
            {row.hint ? (
              <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                {row.hint}
              </p>
            ) : null}
          </th>
          {plans.map((plan) => (
            <td
              key={`${plan.id}-${row.key}`}
              className="border-b border-l border-neutral-200 px-4 py-3 align-top dark:border-neutral-800"
            >
              <PlanCell row={row} plan={plan} onChange={onChange} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function PlanCell({
  row,
  plan,
  onChange,
}: {
  row: PlanRow
  plan: EditablePlan
  onChange: <TKey extends keyof EditablePlan>(
    id: string,
    key: TKey,
    value: EditablePlan[TKey],
  ) => void
}) {
  switch (row.type) {
    case 'boolean':
      return (
        <label className="inline-flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
          <input
            type="checkbox"
            checked={plan[row.key] === true}
            onChange={(event) =>
              onChange(plan.id, row.key, event.target.checked as never)
            }
            className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
          />
          <span>{plan[row.key] === true ? 'Enabled' : 'Disabled'}</span>
        </label>
      )
    case 'multiline':
      return (
        <textarea
          value={String(plan[row.key] ?? '')}
          onChange={(event) =>
            onChange(plan.id, row.key, event.target.value as never)
          }
          rows={row.rows}
          className="w-full resize-y rounded-2xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:ring-blue-900"
        />
      )
    case 'number':
      return (
        <input
          type="number"
          value={toInputValue(
            plan[row.key] as string | number | null | undefined,
          )}
          onChange={(event) =>
            onChange(plan.id, row.key, event.target.value as never)
          }
          className="w-full rounded-2xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:ring-blue-900"
        />
      )
    case 'text':
    default:
      return (
        <input
          type="text"
          value={String(plan[row.key] ?? '')}
          onChange={(event) =>
            onChange(plan.id, row.key, event.target.value as never)
          }
          className="w-full rounded-2xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:ring-blue-900"
        />
      )
  }
}

type PlanRow = {
  key: keyof EditablePlan
  label: string
  type: 'text' | 'number' | 'boolean' | 'multiline'
  hint?: string
  rows?: number
}

type PlanSection = {
  title: string
  rows: Array<PlanRow>
}

const PLAN_SECTIONS: Array<PlanSection> = [
  {
    title: 'Identity',
    rows: [
      {
        key: 'displayName',
        label: 'Display name',
        type: 'text',
        hint: 'Primary label shown to users.',
      },
      {
        key: 'name',
        label: 'Plan key',
        type: 'text',
        hint: 'Stable internal identifier.',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'multiline',
        hint: 'Short explanatory copy for pricing and admin views.',
        rows: 3,
      },
      {
        key: 'ctaLabel',
        label: 'CTA label',
        type: 'text',
        hint: 'Button copy shown on pricing surfaces.',
      },
    ],
  },
  {
    title: 'Pricing & order',
    rows: [
      {
        key: 'pricePenceMonthly',
        label: 'Monthly price (pence)',
        type: 'number',
        hint: 'Use 14500 for £145/month.',
      },
      {
        key: 'priceCurrency',
        label: 'Currency',
        type: 'text',
      },
      {
        key: 'priceLabel',
        label: 'Price label',
        type: 'text',
      },
      {
        key: 'sortOrder',
        label: 'Sort order',
        type: 'number',
      },
    ],
  },
  {
    title: 'Visibility & audience',
    rows: [
      {
        key: 'isHighlighted',
        label: 'Highlighted plan',
        type: 'boolean',
      },
      {
        key: 'isPublic',
        label: 'Visible on public pricing',
        type: 'boolean',
      },
      {
        key: 'availableToAnonymous',
        label: 'Available to anonymous users',
        type: 'boolean',
      },
      {
        key: 'availableToAuthenticated',
        label: 'Available to authenticated users',
        type: 'boolean',
      },
    ],
  },
  {
    title: 'Usage limits',
    rows: [
      {
        key: 'promptLimitTotal',
        label: 'Prompt limit total',
        type: 'number',
        hint: 'Leave blank to persist null.',
      },
      {
        key: 'promptLimitWeekly',
        label: 'Prompt limit weekly',
        type: 'number',
        hint: 'Leave blank to persist null.',
      },
      {
        key: 'promptLimitMonthly',
        label: 'Prompt limit monthly',
        type: 'number',
        hint: 'Leave blank to persist null.',
      },
      {
        key: 'conversationLimitMonthly',
        label: 'Conversation limit monthly',
        type: 'number',
        hint: 'Leave blank to persist null.',
      },
      {
        key: 'imageAnalysisLimitMonthly',
        label: 'Image analysis limit monthly',
        type: 'number',
        hint: 'Leave blank to persist null.',
      },
      {
        key: 'recordLimit',
        label: 'Record limit',
        type: 'number',
      },
      {
        key: 'apiLimit',
        label: 'API limit',
        type: 'number',
      },
      {
        key: 'storageLimitMb',
        label: 'Storage limit (MB)',
        type: 'number',
      },
    ],
  },
  {
    title: 'Capabilities',
    rows: [
      {
        key: 'canUploadFiles',
        label: 'Allow file uploads',
        type: 'boolean',
      },
      {
        key: 'canSearchSemantic',
        label: 'Advanced semantic search',
        type: 'boolean',
      },
      {
        key: 'featureBulletsInput',
        label: 'Feature bullets',
        type: 'multiline',
        hint: 'One bullet per line.',
        rows: 6,
      },
    ],
  },
]

function formatPlanName(value: string) {
  if (!value) return value
  return value.replace('User', '').replace(/^./, (char) => char.toUpperCase())
}

function comparePlans(left: { name: string }, right: { name: string }) {
  const order = [
    'freeUser',
    'registeredUser',
    'premiumUser',
    'proUser',
    'enterpriseUser',
  ]
  const leftIndex = order.indexOf(left.name)
  const rightIndex = order.indexOf(right.name)
  const normalizedLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex
  const normalizedRight =
    rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex
  return normalizedLeft - normalizedRight
}

function toNumberValue(value: string | number | null | undefined) {
  if (value === '' || value === null || value === undefined) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function toNullableNumberValue(value: string | number | null | undefined) {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toInputValue(value: string | number | null | undefined) {
  if (value === null || value === undefined) return ''
  return String(value)
}

function formatFeatureBullets(featureBullets: string | null | undefined) {
  if (!featureBullets) return ''
  try {
    const parsed = JSON.parse(featureBullets)
    if (!Array.isArray(parsed)) return ''
    return parsed
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean)
      .join('\n')
  } catch {
    return ''
  }
}

function toFeatureBulletsJson(value: string) {
  const bullets = value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
  return JSON.stringify(bullets)
}

function normalizePlanForEditor(plan: any) {
  return {
    ...plan,
    featureBulletsInput: formatFeatureBullets(plan.featureBullets),
  }
}

function serializePlanForSave(plan: EditablePlan) {
  return {
    id: plan.id,
    name: plan.name,
    displayName: plan.displayName,
    description: plan.description,
    pricePenceMonthly: toNumberValue(plan.pricePenceMonthly),
    priceCurrency: plan.priceCurrency,
    priceLabel: plan.priceLabel,
    featureBullets: toFeatureBulletsJson(plan.featureBulletsInput),
    ctaLabel: plan.ctaLabel,
    isHighlighted: plan.isHighlighted,
    isPublic: plan.isPublic,
    availableToAnonymous: plan.availableToAnonymous,
    availableToAuthenticated: plan.availableToAuthenticated,
    sortOrder: toNumberValue(plan.sortOrder),
    promptLimitTotal: toNullableNumberValue(plan.promptLimitTotal),
    promptLimitWeekly: toNullableNumberValue(plan.promptLimitWeekly),
    promptLimitMonthly: toNullableNumberValue(plan.promptLimitMonthly),
    conversationLimitMonthly: toNullableNumberValue(
      plan.conversationLimitMonthly,
    ),
    imageAnalysisLimitMonthly: toNullableNumberValue(
      plan.imageAnalysisLimitMonthly,
    ),
    recordLimit: toNumberValue(plan.recordLimit),
    apiLimit: toNumberValue(plan.apiLimit),
    storageLimitMb: toNumberValue(plan.storageLimitMb),
    canUploadFiles: plan.canUploadFiles,
    canSearchSemantic: plan.canSearchSemantic,
  }
}

function buildNewPlanDraft(existingPlans: Array<EditablePlan>) {
  const nextSortOrder =
    existingPlans.reduce(
      (max, plan) => Math.max(max, Number(plan.sortOrder ?? 0)),
      0,
    ) + 10
  const nextIndex = existingPlans.length + 1

  return normalizePlanForEditor({
    id: `plan-custom-${Date.now()}`,
    name: `customPlan${nextIndex}`,
    displayName: `Custom ${nextIndex}`,
    description: '',
    pricePenceMonthly: 0,
    priceCurrency: 'GBP',
    priceLabel: '',
    featureBullets: '[]',
    ctaLabel: '',
    isHighlighted: false,
    isPublic: false,
    availableToAnonymous: false,
    availableToAuthenticated: true,
    sortOrder: nextSortOrder,
    promptLimitTotal: null,
    promptLimitWeekly: null,
    promptLimitMonthly: null,
    conversationLimitMonthly: null,
    imageAnalysisLimitMonthly: null,
    recordLimit: 0,
    apiLimit: 0,
    storageLimitMb: 0,
    canUploadFiles: false,
    canSearchSemantic: false,
  })
}
